import { Grid, GridItem, Box, VStack } from '@everlywell/ui-kit';
import { SectionHeader } from 'components/SectionHeader';
import React from 'react';
import { IContentWithImage } from 'utils/types';

import Carousel from '../Carousel';
import StepCard from '../StepCard';

export interface StepCardListProps {
  listOfContentWithImages: IContentWithImage[];
  backgroundColor?: string;
  eyebrow: string;
  heading: string;
  description?: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  } | null;
}

const StepCardList: React.FC<StepCardListProps> = ({
  listOfContentWithImages,
  backgroundColor,
  eyebrow,
  heading,
  description,
}) => {
  const isCardsOnNewRow = React.useMemo(
    () => (listOfContentWithImages?.length ?? 0) > 2,
    [listOfContentWithImages?.length],
  );

  if (!listOfContentWithImages) {
    return null;
  }

  return (
    <Box backgroundColor={backgroundColor} position="relative" px="4">
      <Box
        data-testid="how-it-works"
        py={['3rem', '4rem', '6rem']}
        width="100%"
        maxWidth="1440px"
        mx="auto"
      >
        <SectionHeader
          eyebrow={eyebrow}
          title={heading}
          // @ts-ignore
          description={description?.description}
          align="left"
          titleMaxWidth="75%"
        />
        <Grid alignEdges gap="32px" w="100%" mt={['40px', '80px']}>
          <GridItem width={[1, isCardsOnNewRow ? 1 : 2 / 3]}>
            {/* mobile and tablet */}
            <VStack
              spacing={8}
              display={{
                base: 'flex',
                largeTablet: 'none',
              }}
              maxWidth="450px"
              marginX="auto"
            >
              {listOfContentWithImages.map((card, index) => (
                <StepCard
                  card={{
                    ...card,
                    image: { url: card.image?.url || '' },
                    heading: card.heading || '',
                    description: {
                      childMarkdownRemark: {
                        rawMarkdownBody:
                          card.description?.childMarkdownRemark
                            ?.rawMarkdownBody || '',
                      },
                    },
                  }}
                  index={index}
                  key={index}
                  isLastCard={index === listOfContentWithImages.length - 1}
                />
              ))}
            </VStack>
            {/* desktop */}
            <Box
              display={{
                base: 'none',
                largeTablet: 'initial',
              }}
              sx={{
                '& .swiper': {
                  overflow: [null, null, 'visible'],
                },

                '& .swiper-wrapper': {
                  alignItems: 'stretch',
                },
                '& .swiper-slide': {
                  height: 'auto',
                },
              }}
            >
              <Carousel
                slidesPerView={Math.min(listOfContentWithImages.length, 4)}
                spaceBetween={32}
                showPagination
                arrowPosition="top"
              >
                {listOfContentWithImages.map((card, index) => (
                  <StepCard
                    key={`step-card-${index}`}
                    // @ts-ignore
                    card={card}
                    index={index}
                    isLastCard={index === listOfContentWithImages.length - 1}
                    imageStyles={{
                      aspectRatio: [
                        '1.5',
                        null,
                        null,
                        listOfContentWithImages.length > 3 ? '1.5' : '2',
                      ],
                    }}
                  />
                ))}
              </Carousel>
            </Box>
          </GridItem>
        </Grid>

        {description && description?.childMarkdownRemark?.rawMarkdownBody ? (
          <Box marginTop="4">
            {description.childMarkdownRemark.rawMarkdownBody}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default StepCardList;
